import { flexRender } from '@tanstack/react-table';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import clsx from 'clsx';
import {
  type CSSProperties,
  type JSX,
  memo,
  type PropsWithChildren,
  useMemo,
  useState,
} from 'react';
import { useIntl } from 'react-intl';

import { type StylingProps } from '@dynatrace/strato-components/core';

import {
  horizontalAlignmentCSS,
  verticalAlignmentCSS,
} from './Alignment.sty.js';
import {
  dataTableV2CellActiveInteractionCSS,
  dataTableV2CellInnerCSS,
  dataTableV2CellOuterCSS,
  leftPositionVar,
} from './DataTableV2Cell.sty.js';
import {
  dataTableV2PinnedColumnCSS,
  dataTableV2PrefixColumnCSS,
  dataTableV2RowActionsColumnCSS,
} from './DataTableV2Rows.sty.js';
import { useMergeRefs } from '../../../core/hooks/useMergeRefs.js';
import {
  DataTableV2ColumnContextProvider,
  useDataTableV2,
} from '../contexts/DataTableV2Context.js';
import { DATA_TABLE_V2_DEFAULT_HORIZONTAL_ALIGNMENT } from '../features/ColumnAlignment.js';
import { calculateLeftPinnedPosition } from '../features/ColumnPinning/column-pinning-utils.js';
import type { DataTableV2ColumnType } from '../features/ColumnTypes/ColumnTypes.js';
import { SPACER_COLUMN } from '../features/Layout.js';
import { isRowDetailsColumnDef } from '../features/RowDetails/RowDetails.js';
import { isRowDragColumnDef } from '../features/RowOrder/RowOrder.js';
import { isRowSelectionColumnDef } from '../features/RowSelection/RowSelection.js';
import { SubRowsCellExpander } from '../features/SubRows/SubRowExpanders.js';
import {
  dataTableV2ThresholdPillColor,
  thresholdPillCSS,
} from '../features/Thresholds/Thresholds.sty.js';
import { InlineCellActionMenu } from '../features/UserActions/InlineCellActionsMenu.js';
import { ROW_ACTIONS_COLUMN } from '../features/UserActions/RowActions.js';
import { type Cell, type Column } from '../hooks/useTable/types.js';

interface DataTableV2CellProps<TData, TValue> extends StylingProps {
  dragAndDropRef?: (element: HTMLElement | null) => void;

  cell: Cell<TData, TValue>;

  /**
   * This function measures column sizes and is triggered when the cell is rendered in the DOM.
   */
  columnMeasure?: (element: Element | null) => void;

  /**
   * Column index in order to map it to the cells data attributes for identification.
   */
  columnIndex: number;

  /**
   * Row index in order to map it to the cells data attributes for identification.
   */
  rowIndex?: number;

  /**
   * Stringified cell dependencies that act as a memo invalidation for the memoized
   * component. If something on the table state changes that does not affect the
   * `cell` prop passed here, it can be added to the cellDeps columnDef.meta field
   * in order to use it as cache control.
   */
  cellDeps?: string;

  /**
   * Flag defining if the current cell should be used as a column measurement cell
   */
  shouldMeasureColumn?: boolean;
}

// This component should only receive primitive or memoized props in order
// to optimize render performance when commiting to the DOM.
function InternalMemoizedCell(
  props: PropsWithChildren<{
    /**
     * RowId from the TanStack/table cell instance.
     */
    rowId: string;
    /**
     * RowIndex based on the virtualization index.
     */
    rowIndex?: number;
    /**
     * ColumnId from the TanStack/table cell instance.
     */
    columnId: string;
    /**
     * ColumnIndex based on the virtualization index.
     */
    columnIndex: number;
    className: string;
    style?: CSSProperties;

    /**
     * This function measures column sizes and is triggered when the cell is rendered in the DOM.
     */
    columnMeasure?: (element: Element | null) => void;

    /**
     * Optional ref intended for drag and drop.
     */
    dragAndDropRef?: (element: HTMLElement | null) => void;

    /**
     * Determines whether the DataTableV2 has expanding rows (subRows).
     */
    enableExpanding?: boolean;
    /**
     * Determines if the cell has a custom cell renderer defined by the column definition.
     */
    hasCustomCellRenderer?: boolean;
    /**
     * Determines if the given cell within the column contains the sub row expanding trigger.
     */
    isSubRowIndicatorColumn?: boolean;
    /**
     * Determines the column type for the given cell.
     */
    columnType?: DataTableV2ColumnType;
    /**
     * Determines if the current cell can currently wrap it's lines.
     */
    isLineWrapped?: boolean;
    /**
     * Stringified cell dependencies that act as a memo invalidation for the memoized
     * component. If something on the table state changes that does not affect the
     * `cell` prop passed here, it can be added to the cellDeps columnDef.meta field
     * in order to use it as cache control.
     */
    cellDeps?: string;
    /**
     * Threshold pill color displayed in the first cell in a row.
     * Needs to be a valid css color definition.
     */
    pillColor: string | null;
    /**
     * Color of the cell text, if a given threshold applies to the cell.
     * Needs to be a valid css color definition.
     */
    thresholdTextColor?: string;
    /**
     * Color of the cell background, if a given threshold applies to the cell.
     * Needs to be a valid css color definition.
     */
    thresholdBackgroundColor?: string;
    /**
     * Determines if the current cell can receive user actions and displays a menu trigger.
     */
    canUserAction?: boolean;
    /**
     * Indicates the nesting level of the row.
     */
    subRowDepth: number;
    /**
     * Flag defining if the current cell should be used as a column measurement cell
     */
    shouldMeasureColumn?: boolean;
    /**
     * The table's virtualization container.
     */
    virtualizationContainer?: HTMLDivElement;
    /**
     * Defines css positioning for the left pinned columns.
     */
    leftPosition?: string;
  }>,
): JSX.Element | null {
  const {
    rowId,
    rowIndex,
    columnId,
    columnIndex,
    style,
    className,
    columnMeasure,
    dragAndDropRef,
    pillColor,
    enableExpanding,
    hasCustomCellRenderer,
    isSubRowIndicatorColumn,
    thresholdTextColor,
    thresholdBackgroundColor,
    columnType,
    isLineWrapped,
    children,
    canUserAction,
    subRowDepth,
    shouldMeasureColumn,
    leftPosition,
  } = props;

  const intl = useIntl();

  // Handling the hovered state via React instead of via css allows us
  // to omit the button from the default render, which will reduce the
  // amount of elements rendered quite heavily.
  const [hovered, setHovered] = useState(false);
  const mergedRef = useMergeRefs<HTMLElement | null>([
    dragAndDropRef,
    columnMeasure,
  ]);

  return (
    <div
      role={columnId === SPACER_COLUMN.id ? 'presentation' : 'cell'}
      ref={mergedRef}
      data-index={`row-${rowIndex}-column-${columnIndex}`}
      data-columnid={columnId}
      data-column={columnIndex}
      data-columntype={columnType}
      data-subrow-depth={subRowDepth}
      data-columnmeasure={shouldMeasureColumn || true}
      className={className}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        ...assignInlineVars({
          [dataTableV2ThresholdPillColor]: pillColor,
          [leftPositionVar]: leftPosition,
        }),
        color: thresholdTextColor,
        backgroundColor: thresholdBackgroundColor,
        ...style,
        minWidth:
          columnId === ROW_ACTIONS_COLUMN.id ? 'max-content' : undefined,
      }}
    >
      {/* Only render the subrow expander in the first visible column and if no custom cell renderer is exposed */}
      {enableExpanding === true &&
      !hasCustomCellRenderer &&
      isSubRowIndicatorColumn === true ? (
        <SubRowsCellExpander />
      ) : null}

      {!hasCustomCellRenderer &&
      columnType !== 'sparkline' &&
      columnType !== 'meterbar' &&
      columnType !== 'log-content' ? (
        <div
          className={clsx(
            dataTableV2CellInnerCSS({
              lineWrap: isLineWrapped,
            }),
          )}
        >
          {children}
        </div>
      ) : (
        children
      )}
      {canUserAction && (
        <InlineCellActionMenu
          rowId={rowId}
          columnId={columnId}
          type="cell"
          aria-label={intl.formatMessage({
            defaultMessage: 'Trigger cell action',
            description: 'Aria label for DataTable cell actions trigger',
            id: 'SOikNJR+NVkSUago',
          })}
          data-column={columnIndex}
          hovered={hovered}
        />
      )}
    </div>
  );
}

const DataTableV2CellMemo = memo(InternalMemoizedCell);

export function DataTableV2Cell<TData, TValue>(
  props: DataTableV2CellProps<TData, TValue>,
): JSX.Element | null {
  const {
    cell,
    dragAndDropRef,
    columnMeasure,
    style: consumerStyle,
    className: consumerClassName,
    rowIndex,
    columnIndex,
    cellDeps,
    shouldMeasureColumn,
  } = props;

  const table = useDataTableV2();

  const visualVariantOptions = table.getVisualVariantOptions();

  const columnDef = cell.column.columnDef;

  // thresholds - both row and cell highlighting
  const pillColor = columnIndex === 0 ? cell.row.getThresholdPill() : null;
  const thresholdCellColors = cell.getThresholdColors();

  const leftPinnedColumns = table.getState().columnPinning.left;
  const columnId = cell.column.id;
  const columnPinnedIndex = cell.column.getIndex('left');
  const rowDensity = visualVariantOptions.rowDensity;
  const isRowThresholdEnabled =
    table.options.hasThresholds &&
    table.options.rowThresholds?.some((threshold) => threshold.type === 'pill');

  const leftPosition = useMemo(
    () =>
      calculateLeftPinnedPosition(
        columnPinnedIndex,
        leftPinnedColumns,
        rowDensity,
        isRowThresholdEnabled,
      ),
    [columnPinnedIndex, leftPinnedColumns, rowDensity, isRowThresholdEnabled],
  );

  const isPrefixColumn: boolean =
    isRowSelectionColumnDef(cell.column.columnDef) ||
    isRowDetailsColumnDef(cell.column.columnDef) ||
    isRowDragColumnDef(cell.column.columnDef);

  const isPinnedColumn = cell.column.getIsPinned();
  const pinnedColumnBg =
    isPinnedColumn &&
    visualVariantOptions.rowSeparation !== 'zebraStripes' &&
    (visualVariantOptions.contained ? 'contained' : 'uncontained');

  return (
    <DataTableV2CellMemo
      rowId={cell.row.id}
      rowIndex={rowIndex}
      columnIndex={columnIndex}
      columnId={columnId}
      subRowDepth={cell.row.getSubRowDepth()}
      canUserAction={cell.getCanUserActions()}
      columnMeasure={columnMeasure}
      shouldMeasureColumn={shouldMeasureColumn}
      dragAndDropRef={dragAndDropRef}
      style={consumerStyle}
      pillColor={pillColor}
      thresholdTextColor={thresholdCellColors.color}
      thresholdBackgroundColor={thresholdCellColors.backgroundColor}
      enableExpanding={table.options.enableExpanding}
      leftPosition={leftPosition}
      hasCustomCellRenderer={cell.column.columnDef.meta?.hasCustomCellRenderer}
      isSubRowIndicatorColumn={
        table.options.enableExpanding === true &&
        table.getSubRowIndicatorColumnId() === columnId
      }
      columnType={cell.column.columnDef.columnType}
      isLineWrapped={cell.column.getIsLineWrapped()}
      virtualizationContainer={table.getVirtualizationContainer()}
      className={clsx(
        dataTableV2CellOuterCSS({
          lineWrap: cell.column.getIsLineWrapped(),
          fontStyle: cell.column.getFontStyle(),
          rowDensity: columnDef.meta?.hasCustomCellRenderer
            ? 'customCellRenderer'
            : visualVariantOptions.rowDensity,
          interactiveRows: table.options.enableRowInteractivity,
          loading: table.getState().loading,
          verticalDividers: visualVariantOptions.verticalDividers,
          pinnedColumn: isPinnedColumn,
          background: pinnedColumnBg,
        }),
        horizontalAlignmentCSS[
          columnDef.alignment ?? DATA_TABLE_V2_DEFAULT_HORIZONTAL_ALIGNMENT
        ],
        verticalAlignmentCSS[cell.column.getVerticalAlignment()],
        {
          [thresholdPillCSS]: pillColor !== undefined && pillColor !== null,
          [dataTableV2PrefixColumnCSS]: isPrefixColumn,
          [dataTableV2PinnedColumnCSS]: isPinnedColumn !== false,
          [dataTableV2RowActionsColumnCSS]: columnId === ROW_ACTIONS_COLUMN.id,
          // TODO: Meh, this is not really nice
          [`${dataTableV2CellActiveInteractionCSS}`]:
            table.getState().floatingMenuInfo?.rowId === cell.row.id &&
            table.getState().floatingMenuInfo?.columnId === columnId,
        },
        consumerClassName,
      )}
      cellDeps={cellDeps}
    >
      {cell.column.columnDef.meta?.hasCustomCellRenderer ? (
        <DataTableV2ColumnContextProvider
          value={cell.column as Column<unknown, unknown>}
        >
          {flexRender(columnDef.cell, cell.getContext())}
        </DataTableV2ColumnContextProvider>
      ) : (
        flexRender(columnDef.cell, cell.getContext())
      )}
    </DataTableV2CellMemo>
  );
}
